import { useEffect, useMemo, useState } from "react";
import { normalizeForSearch } from "../../utils/normalizeSearch";

export default function ServiceSearch({
  services = [],
  value = "",
  onChange,
  onPick,
  placeholder = "Search services...",
  maxSuggestions = 10,
}) {
  const [forceClosed, setForceClosed] = useState(false);
  const query = normalizeForSearch(value).trim();

  const matches = useMemo(() => {
    if (!query) return [];
    const list = Array.isArray(services) ? services : [];
    const filtered = list.filter((service) => {
      const nameMatch = normalizeForSearch(service?.name).includes(query);
      const skuMatch = normalizeForSearch(service?.sku).includes(query);
      return nameMatch || skuMatch;
    });
    filtered.sort((a, b) => {
      const aName = normalizeForSearch(a?.name);
      const bName = normalizeForSearch(b?.name);
      const aStarts = aName.startsWith(query);
      const bStarts = bName.startsWith(query);
      if (aStarts !== bStarts) return aStarts ? -1 : 1;
      return aName.localeCompare(bName);
    });
    return filtered.slice(0, Math.max(0, maxSuggestions));
  }, [services, query, maxSuggestions]);

  useEffect(() => {
    setForceClosed(false);
  }, [query]);

  const showSuggestions =
    !forceClosed && query.length >= 2 && matches.length > 0;

  const handleInputChange = (event) => {
    onChange?.(event.target.value);
  };

  const handleClear = () => {
    onChange?.("");
    setForceClosed(true);
  };

  const handlePick = (service) => {
    onPick?.(service);
    setForceClosed(true);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      setForceClosed(true);
      return;
    }
    if (event.key === "Enter" && showSuggestions && matches.length > 0) {
      event.preventDefault();
      handlePick(matches[0]);
    }
  };

  return (
    <div className="relative">
      <input
        type="text"
        value={value}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={() => setForceClosed(true)}
        placeholder={placeholder}
        className="w-full h-11 md:h-12 px-4 pr-10 text-base md:text-lg rounded-xl border border-slate-300 focus:border-slate-300 focus:outline-none focus:ring-2 focus:ring-slate-200"
      />
      {value ? (
        <button
          type="button"
          onClick={handleClear}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          aria-label="Clear search"
        >
          X
        </button>
      ) : null}
      {showSuggestions ? (
        <div className="absolute z-20 mt-2 w-full rounded-xl border border-slate-200 bg-white shadow-lg">
          <ul className="max-h-56 overflow-auto py-1">
            {matches.map((service) => (
              <li key={service.id}>
                <button
                  type="button"
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => handlePick(service)}
                  className="w-full px-3 py-2 text-left text-sm md:text-base text-slate-700 hover:bg-slate-50"
                >
                  <span className="font-semibold text-slate-900">
                    {service.name}
                  </span>
                  {service.sku ? (
                    <span className="block text-xs text-slate-500">
                      SKU: {service.sku}
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
